import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { callProfileBuilder, getProfileCached } from "@/lib/profileBuilderApi";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChipInput } from "@/components/ui/chip-input";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ArrowRight, Check, ChevronDown, ChevronRight, Loader2, Package, Plus, RefreshCw, Sparkles } from "lucide-react";
import { CITIES_BY_COUNTRY } from "@/lib/audienceOptions";
import { marketFromCountries, type CampaignSuggestion } from "@/components/ai-knowledge/CampaignProfileBuilderDialog";
import { GOALS, PRODUCT_GOALS, SIZE_OPTIONS, SKIP_SUGGESTIONS, fromSaved, isAnswered, isIndianPlace, pickAbroadCountry, setAbroadCities, prefill, shortProductName, stepsFor, toRequest, type Answers, type FlowContext, type GoalKey, type Question as Q } from "./goalFlows";
import { BuildingState, ChoiceRow, GoalTile, PillToggle, Question, StepShell } from "./parts";

interface Props {
  companyProfileId: string;
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guestToken?: string;
  onCreated: (knowledgeId: string) => void;
  editKnowledgeId?: string | null;
  /** Called when a saved campaign cannot be shown in this wizard. */
  onCannotEdit?: () => void;
}

const ABROAD = ["UAE", "Saudi Arabia", "USA", "UK", "Singapore", "Qatar", "Germany", "Australia", "Canada"];
const INDIA_CITIES = ["Mumbai", "Pune", "Bengaluru", "Delhi NCR", "Chennai", "Hyderabad", "Ahmedabad", "Kolkata"];
const KNOWN_PLACES = ["India", ...ABROAD, "Remote"];
const JOB_INDUSTRIES = ["SaaS", "Fintech", "Healthcare", "E-commerce", "Manufacturing", "Consulting"];
const isIndian = (c: string) => c.toLowerCase() === "india" || INDIA_CITIES.some((x) => x.toLowerCase() === c.toLowerCase()) || marketFromCountries([c]) === "india";

const call = callProfileBuilder;

type Phase = "goal" | "q" | "suggest" | "finish" | "review";
interface EditData { suggestion: CampaignSuggestion; title: string; reach: "new" | "existing"; pricing: { disclose: boolean; mode?: string; text: string }; goalFields: Record<string, unknown>; tone?: string }

export function StrategyWizard({ companyProfileId, workspaceId, open, onOpenChange, guestToken, onCreated, editKnowledgeId, onCannotEdit }: Props) {
  const queryClient = useQueryClient();
  const base = { workspaceId: guestToken ? null : workspaceId, guestToken: guestToken ?? null, profileId: companyProfileId };
  const [ctx, setCtx] = useState<FlowContext | null>(null);
  const [phase, setPhase] = useState<Phase>("goal");
  const [goal, setGoal] = useState<GoalKey | null>(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [moreGoals, setMoreGoals] = useState(false);
  const [moreProducts, setMoreProducts] = useState(false);
  const [customProduct, setCustomProduct] = useState(false);
  const [suggestions, setSuggestions] = useState<CampaignSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState<CampaignSuggestion | null>(null);
  const [titles, setTitles] = useState<string[]>([]);
  const [adjust, setAdjust] = useState(false);
  const [refineText, setRefineText] = useState("");
  const [refining, setRefining] = useState(false);
  const [preview, setPreview] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const previewFor = useRef("");
  const isEdit = !!editKnowledgeId;
  const [edit, setEdit] = useState<EditData | null>(null);
  /** "one" = editing a single row, "walk" = goal changed, walk all its questions. */
  const [editMode, setEditMode] = useState<"one" | "walk" | null>(null);
  const [moreOpts, setMoreOpts] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhase("goal"); setGoal(null); setStep(0); setAnswers({}); setSuggestions([]); setPicked(null); setPreview(""); setMoreGoals(false); setAdjust(false); previewFor.current = "";
    setCtx(null);
    getProfileCached(base).then((p) => {
      const f = p?.extracted_facts ?? {};
      const countries = [...(Array.isArray(f.countries) ? f.countries : []), ...(Array.isArray(f.regions) ? f.regions : [])]
        .map((x: any) => String(typeof x === "string" ? x : x?.name ?? "").trim())
        .filter((x: string) => KNOWN_PLACES.some((c) => c.toLowerCase() === x.toLowerCase()));
      setCtx({
        products: (f.products ?? []).filter((x: any) => String(x?.name ?? "").trim()).map((x: any) => ({ name: x.name, description: x.description })),
        industries: (Array.isArray(f.industries_served) ? f.industries_served : []).map(String).filter(Boolean),
        countries: Array.from(new Set(countries)).slice(0, 5),
        lastAsk: p?.last_ask ?? null,
      });
    }).catch(() => setCtx({ products: [], industries: [], countries: [], lastAsk: null }));
    setEdit(null); setEditMode(null); setMoreOpts(false);
    if (editKnowledgeId) {
      call({ ...base, action: "get_campaign_profile", campaignKnowledgeId: editKnowledgeId }).then((res) => {
        const v2 = res.v2 ?? {};
        const saved = fromSaved(v2);
        if (!saved) { onCannotEdit?.(); return; }
        const c = v2.confirmed ?? {};
        setGoal(saved.goal); setAnswers(saved.answers);
        setTitles(Array.isArray(v2.buyer_titles) && v2.buyer_titles.length ? v2.buyer_titles : v2.suggestion.buyer_titles ?? []);
        setEdit({
          suggestion: v2.suggestion, title: res.title ?? v2.suggestion.title, reach: c.reach === "existing" ? "existing" : "new",
          pricing: c.pricing ? { disclose: !!c.pricing.disclose, mode: c.pricing.mode, text: c.pricing.text ?? "" } : { disclose: false, text: "" },
          goalFields: c.goal_fields && typeof c.goal_fields === "object" ? c.goal_fields : {}, tone: c.tone || undefined,
        });
        setPhase("review");
      }).catch((e) => { toast.error(e.message); onOpenChange(false); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyProfileId, editKnowledgeId]);

  const needsPrefill = useRef(false);
  useEffect(() => {
    if (ctx && goal && needsPrefill.current) { needsPrefill.current = false; setAnswers((a) => ({ ...prefill(goal, ctx), ...a })); }
  }, [ctx, goal]);
  const flow: Q[] = goal && ctx ? stepsFor(goal, ctx) : [];
  const q = flow[step];
  const set = (k: string, v: Answers[string]) => setAnswers((a) => ({ ...a, [k]: v }));
  const req = useMemo(() => (goal ? toRequest(goal, answers) : null), [goal, answers]);
  const canNext = q ? isAnswered(q, answers) : false;

  const chooseGoal = (g: GoalKey) => {
    if (isEdit && g === goal) return toReview();
    needsPrefill.current = !ctx;
    setGoal(g); setAnswers(ctx ? prefill(g, ctx) : {});
    setMoreProducts(false); setCustomProduct(false); setSuggestions([]); setPicked(null);
    if (isEdit) setEditMode("walk");
    setTimeout(() => { setStep(0); setPhase("q"); }, 180);
  };
  const toReview = () => { setEditMode(null); setPhase("review"); };
  const next = () => {
    if (editMode === "one") return toReview();
    if (step < flow.length - 1) setStep(step + 1); else if (isEdit) toReview(); else build();
  };
  const pickAndGo = (k: string, v: string) => {
    set(k, v);
    setTimeout(() => { if (editMode === "one") toReview(); else if (step < flow.length - 1) setStep(step + 1); else if (isEdit) toReview(); }, 220);
  };
  const back = () => {
    if (isEdit) { if (phase === "q" && editMode === "walk" && step > 0) return setStep(step - 1); return toReview(); }
    if (phase === "finish") return setPhase("suggest");
    if (phase === "suggest") { setPhase("q"); setStep(flow.length - 1); return; }
    if (step > 0) setStep(step - 1); else setPhase("goal");
  };

  const build = async (refresh = false) => {
    if (!req) return;
    setPhase("suggest"); setLoading(true);
    try {
      const res = await call({
        ...base, action: "suggest_campaigns", refresh, countries: req.countries, products: req.products, one_product_per_campaign: true,
        industries: req.industries, company_sizes: req.companySizes, market: marketFromCountries(req.countries) || "both",
        goal: req.goal, goalCustom: req.goalCustom, goalDetails: req.goalDetails, sourceUrl: req.sourceUrl,
      });
      setSuggestions(res.suggestions ?? []);
    } catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const pick = (s: CampaignSuggestion) => { setPicked(s); setTitles(s.buyer_titles ?? []); setPreview(""); setAdjust(false); setPhase("finish"); };

  const goalBody = () => ({ goal: req!.goal, goalCustom: req!.goalCustom, goalDetails: req!.goalDetails });
  const loadPreview = async (s = picked, t = titles) => {
    if (!s || !req) return;
    setPreviewing(true);
    try {
      const res = await call({ ...base, ...goalBody(), action: "preview_first_message", suggestion: { ...s, region: req.countries.join(", ") }, confirm: { buyer_titles: t, reach: "new", first_ask: req.firstAsk, ask_details: req.askDetails } });
      setPreview(res.message ?? "");
    } catch (e: any) { toast.error(e.message); }
    finally { setPreviewing(false); }
  };
  useEffect(() => {
    if (phase !== "finish" || !picked) return;
    const k = `${picked.title}|${picked.product}`;
    if (previewFor.current === k) return;
    previewFor.current = k; loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, picked]);

  const refine = async () => {
    if (!picked || !refineText.trim() || !req) return;
    setRefining(true);
    try {
      const res = await call({ ...base, ...goalBody(), action: "refine_suggestion", instruction: refineText.trim(), suggestion: { ...picked, region: req.countries.join(", ") } });
      const s = res.suggestion as CampaignSuggestion;
      previewFor.current = `${s.title}|${s.product}`;
      setPicked(s); setTitles(s.buyer_titles ?? []); setRefineText(""); setPreview("");
      loadPreview(s, s.buyer_titles ?? []);
      try { localStorage.removeItem(`audience-ctx:${editKnowledgeId}`); } catch { /* ignore */ }
      queryClient.removeQueries({ queryKey: ["audience-ctx", editKnowledgeId] });
      toast.success("Campaign updated");
    } catch (e: any) { toast.error(e.message); }
    finally { setRefining(false); }
  };

  const save = async () => {
    if (!picked || !req) return;
    if (!titles.length) { toast.error("Add at least one job title"); setAdjust(true); return; }
    setSaving(true);
    try {
      const region = req.countries.join(", ");
      const res = await call({
        ...base, action: "create_campaign_profile", suggestion: picked, title: picked.title,
        confirm: {
          goal: req.goal, goal_custom: req.goalCustom, goal_details: req.goalDetails, goal_fields: req.goalFields,
          company_size: req.companySizes.join(", "), company_sizes: req.companySizes, region, countries: req.countries,
          market: marketFromCountries(req.countries) || "both", first_ask: req.firstAsk, reach: "new", ask_details: req.askDetails, do_not_contact: req.doNotContact,
          buyer_titles: titles, products: req.products.length ? req.products : [], pricing: { disclose: false, text: "" },
        },
      });
      toast.success("Campaign strategy saved");
      onCreated(res.id); onOpenChange(false);
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!edit || !req || !goal) return;
    if (!titles.length) return toast.error("Add at least one job title");
    if (flow.some((x) => !isAnswered(x, answers))) return toast.error("Some answers are missing");
    setSaving(true);
    try {
      const product = PRODUCT_GOALS.includes(goal);
      const res = await call({
        ...base, action: "update_campaign_profile", campaignKnowledgeId: editKnowledgeId, suggestion: edit.suggestion, title: edit.title.trim() || edit.suggestion.title,
        confirm: {
          goal: req.goal, goal_custom: req.goalCustom, goal_details: req.goalDetails, goal_fields: { ...edit.goalFields, ...req.goalFields },
          company_size: req.companySizes.join(", "), company_sizes: req.companySizes, region: req.countries.join(", "), countries: req.countries,
          market: marketFromCountries(req.countries) || "both", first_ask: req.firstAsk, reach: edit.reach, tone: edit.tone, ask_details: req.askDetails, do_not_contact: req.doNotContact,
          buyer_titles: titles, products: req.products, pricing: product ? edit.pricing : { disclose: false, text: "" },
        },
      });
      toast.success("Campaign updated");
      onCreated(res.id ?? editKnowledgeId!); onOpenChange(false);
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  /* ---------- question screens ---------- */
  const renderQuestion = (q: Q) => {
    if (q.kind === "product") {
      const list = ctx?.products ?? [];
      const shown = moreProducts ? list : list.slice(0, 6);
      const cur = String(answers.product ?? "");
      const isCustom = customProduct || (!!cur && !list.some((p) => p.name === cur));
      return (
        <Question title={q.title}>
          <div className="space-y-2">
            {shown.map((p) => <ChoiceRow key={p.name} active={cur === p.name} onClick={() => { setCustomProduct(false); pickAndGo("product", p.name); }} icon={Package} label={shortProductName(p.name)} title={p.name} />)}
            {list.length > 6 && !moreProducts && (
              <button type="button" onClick={() => setMoreProducts(true)} className="flex w-full items-center justify-center gap-1 py-2 text-sm text-muted-foreground hover:text-foreground">Show {list.length - 6} more<ChevronDown className="h-3.5 w-3.5" /></button>
            )}
            {isCustom ? (
              <Input autoFocus className="h-14 rounded-xl text-base" value={list.some((p) => p.name === cur) ? "" : cur} onChange={(e) => set("product", e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && canNext) next(); }} placeholder="Type the product or service" />
            ) : (
              <button type="button" onClick={() => { setCustomProduct(true); set("product", ""); }} className="flex w-full items-center justify-center gap-1 py-2 text-sm text-muted-foreground hover:text-foreground"><Plus className="h-3.5 w-3.5" />Something else</button>
            )}
          </div>
        </Question>
      );
    }
    if (q.kind === "single") {
      return <Question title={q.title}><div className="space-y-2">{q.options!.map((o) => <ChoiceRow key={o.v} active={answers[q.id] === o.v} onClick={() => pickAndGo(q.id, o.v)} icon={o.icon} label={o.label} />)}</div></Question>;
    }
    if (q.kind === "multi") {
      const cur = (answers[q.id] as string[]) ?? [];
      return <Question title={q.title}><div className="space-y-2">{q.options!.map((o) => { const on = cur.includes(o.v); return <ChoiceRow key={o.v} active={on} onClick={() => set(q.id, on ? cur.filter((x) => x !== o.v) : [...cur, o.v])} icon={o.icon} label={o.label} />; })}</div></Question>;
    }
    if (q.kind === "who") {
      const inds = (answers.industries as string[]) ?? [];
      const sizes = (answers.sizes as string[]) ?? [];
      const opts = Array.from(new Set([...(ctx?.industries ?? []).slice(0, 10)])).map((v) => ({ v, label: v }));
      return (
        <Question title={q.title}>
          <PillToggle options={opts} value={inds} onChange={(v) => set("industries", v)} addLabel="Add industry" />
          {q.withSizes && (
            <div className="mt-10">
              <p className="mb-3 text-center text-sm text-muted-foreground">Company size</p>
              <div className="flex flex-wrap justify-center gap-2">
                <button type="button" onClick={() => set("sizes", [])} aria-pressed={!sizes.length}
                  className={cn("inline-flex h-10 items-center gap-1.5 rounded-full border px-4 text-sm font-medium transition-colors", !sizes.length ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40")}>
                  {!sizes.length && <Check className="h-3.5 w-3.5" />}Any size
                </button>
                <PillToggle noAdd className="contents" options={SIZE_OPTIONS.map((o) => ({ v: o.v, label: o.label }))} value={sizes} onChange={(v) => set("sizes", v)} />
              </div>
            </div>
          )}
        </Question>
      );
    }
    if (q.kind === "chips") {
      const sugg = q.suggestions ?? (q.id === "industries" ? Array.from(new Set([...(ctx?.industries ?? []), ...JOB_INDUSTRIES])).slice(0, 10) : []);
      return <Question title={q.title}><PillToggle options={sugg.map((v) => ({ v, label: v }))} value={(answers[q.id] as string[]) ?? []} onChange={(v) => set(q.id, v)} /></Question>;
    }
    if (q.kind === "text") {
      return (
        <Question title={q.title}>
          <div className="space-y-3">
            <Input autoFocus className="h-14 rounded-xl text-base" value={String(answers[q.id] ?? "")} onChange={(e) => set(q.id, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && canNext) next(); }} placeholder={q.placeholder} />
            {q.link && <Input className="h-12 rounded-xl" value={String(answers[`${q.id}_link`] ?? "")} onChange={(e) => set(`${q.id}_link`, e.target.value)} placeholder={`${q.link.label} — ${q.link.placeholder}`} />}
          </div>
        </Question>
      );
    }
    if (q.kind === "where") {
      const cs = (answers.countries as string[]) ?? [];
      const market = (answers.market as string) ?? (cs.length && !isIndianPlace(cs[0]) ? "abroad" : "india");
      const indiaOn = market === "india";
      const abroadOn = market === "abroad";
      const toggleIndia = () => { if (!indiaOn) setAnswers((a) => ({ ...a, market: "india", countries: ["India"] })); };
      const toggleAbroad = () => { if (!abroadOn) setAnswers((a) => ({ ...a, market: "abroad", countries: [] })); };
      const setIndia = (v: string[]) => {
        // "All of India" and specific cities are exclusive.
        const added = v.find((x) => !cs.includes(x));
        let next = v;
        if (added === "India") next = ["India"];
        else if (added) next = v.filter((x) => x !== "India");
        set("countries", next.length ? next : ["India"]);
      };
      const country = abroadOn ? cs[0] : undefined;
      const cities = country ? cs.slice(1) : [];
      const alias: Record<string, string> = { USA: "United States", UK: "United Kingdom" };
      const cityOpts = country ? (CITIES_BY_COUNTRY[country] ?? CITIES_BY_COUNTRY[alias[country] ?? ""] ?? []) : [];
      const setCities = (v: string[]) => {
        const added = v.find((x) => !cities.includes(x));
        if (added === "__all" || !v.filter((x) => x !== "__all").length) return set("countries", [country!]);
        set("countries", setAbroadCities(cs, v.filter((x) => x !== "__all")));
      };
      const big = (on: boolean, label: string, onClick: () => void) => (
        <button type="button" onClick={onClick} aria-pressed={on}
          className={cn("flex h-14 flex-1 items-center justify-center gap-2 rounded-xl border text-base font-medium transition-colors", on ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40")}>
          {on && <Check className="h-4 w-4" />}{label}
        </button>
      );
      return (
        <Question title={q.title}>
          <div className="flex gap-3">{big(indiaOn, "India", toggleIndia)}{big(abroadOn, "Abroad", toggleAbroad)}</div>
          {indiaOn && (
            <div className="mt-8 animate-in fade-in">
              <p className="mb-3 text-center text-sm text-muted-foreground">Which city?</p>
              <PillToggle options={[{ v: "India", label: "All of India" }, ...INDIA_CITIES.map((v) => ({ v, label: v }))]} value={cs} onChange={setIndia} addLabel="City" />
            </div>
          )}
          {abroadOn && (
            <div className="mt-8 animate-in fade-in">
              <p className="mb-3 text-center text-sm text-muted-foreground">Which country?</p>
              <PillToggle single options={ABROAD.map((v) => ({ v, label: v }))} value={country ? [country] : []} onChange={(v) => set("countries", v.length ? pickAbroadCountry(cs, v[0]) : [])} addLabel="Country" />
            </div>
          )}
          {abroadOn && country && (
            <div key={country} className="mt-8 animate-in fade-in">
              <p className="mb-3 text-center text-sm text-muted-foreground">Which city?</p>
              <PillToggle options={[{ v: "__all", label: `All of ${country}` }, ...cityOpts.map((v) => ({ v, label: v }))]} value={cities.length ? cities : ["__all"]} onChange={setCities} addLabel="City" />
            </div>
          )}
        </Question>
      );
    }
    if (q.kind === "skip") {
      const cur = (answers.skip as string[]) ?? [];
      const opts = Array.from(new Set([...SKIP_SUGGESTIONS, ...cur])).map((v) => ({ v, label: v }));
      return <Question title={q.title}><PillToggle options={opts} value={cur} onChange={(v) => set("skip", v)} addLabel={q.placeholder} /></Question>;
    }
    // cta
    return (
      <Question title={q.title}>
        <div className="space-y-2">{q.options!.map((o) => (
          <ChoiceRow key={o.v} active={answers.cta === o.v} onClick={() => set("cta", o.v)} icon={o.icon} label={o.label}>
            {o.link && <Input value={String(answers.cta_link ?? "")} onChange={(e) => set("cta_link", e.target.value)} placeholder={`${o.link.label} (optional)`} className="bg-background" />}
            {o.phone && <Input type="tel" value={String(answers.cta_phone ?? "")} onChange={(e) => set("cta_phone", e.target.value)} placeholder="Phone or WhatsApp (optional)" className="bg-background" />}
          </ChoiceRow>
        ))}</div>
      </Question>
    );
  };

  /* ---------- layout ---------- */
  const total = isEdit ? 0 : goal ? flow.length + 3 : 7;
  const current = phase === "goal" ? 0 : phase === "q" ? step + 1 : phase === "suggest" ? flow.length + 1 : flow.length + 2;
  const autoAdvance = q && (q.kind === "single" || (q.kind === "product" && !customProduct && (ctx?.products.length ?? 0) > 0 && !(answers.product && !(ctx?.products ?? []).some((p) => p.name === answers.product))));
  const isLast = step === flow.length - 1;

  let body: React.ReactNode = null;
  let footer: React.ReactNode = null;

  if (!ctx && (isEdit || phase !== "goal")) {
    body = <div className="flex h-full min-h-[320px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  } else if (isEdit && !edit) {
    body = <div className="flex h-full min-h-[320px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  } else if (phase === "review" && edit && goal && req) {
    const label = (qq: Q) => {
      if (qq.kind === "product") return shortProductName(String(answers.product ?? "")) || "—";
      if (qq.kind === "where") return req.countries.join(", ") || "—";
      if (qq.kind === "who") return [((answers.industries as string[]) ?? []).slice(0, 2).join(", ") || "Any industry", ((answers.sizes as string[]) ?? []).length ? (answers.sizes as string[]).join(", ") : "Any size"].join(" · ");
      if (qq.kind === "cta") return String(answers.cta ?? "") || "—";
      if (qq.kind === "skip") return ((answers.skip as string[]) ?? []).join(", ") || "Nobody";
      const v = answers[qq.id];
      if (qq.options && !Array.isArray(v)) return qq.options.find((o) => o.v === v)?.label ?? (v || "—");
      return Array.isArray(v) ? v.join(", ") || "—" : String(v ?? "") || "—";
    };
    const row = (k: string, name: string, value: string, onClick: () => void, missing = false) => (
      <button key={k} type="button" onClick={onClick} className="group flex w-full min-w-0 items-center gap-4 border-b py-3.5 text-left last:border-b-0">
        <span className="w-28 shrink-0 text-sm text-muted-foreground">{name}</span>
        <span className={cn("min-w-0 flex-1 truncate text-sm font-medium", missing && "text-destructive")}>{value}</span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
      </button>
    );
    const goalLabel = GOALS.find((g) => g.v === goal)?.label ?? goal;
    const isProduct = PRODUCT_GOALS.includes(goal);
    body = (
      <div className="w-full min-w-0 animate-in fade-in duration-300">
        <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} aria-label="Campaign name"
          className="mb-6 h-auto border-0 border-b border-transparent px-0 text-center text-2xl font-semibold tracking-tight shadow-none hover:border-border focus-visible:border-primary focus-visible:ring-0" />
        <div className="rounded-2xl border px-4">
          {row("goal", "Goal", goalLabel, () => setPhase("goal"))}
          {flow.map((qq, i) => row(qq.id, qq.summary, label(qq), () => { setEditMode("one"); setStep(i); setPhase("q"); }, !isAnswered(qq, answers)))}
        </div>
        <div className="mt-6">
          <p className="mb-3 text-center text-sm text-muted-foreground">Job titles</p>
          <PillToggle options={titles.map((v) => ({ v, label: v }))} value={titles} onChange={setTitles} addLabel="Title" />
        </div>
        <button type="button" onClick={() => setMoreOpts((v) => !v)} className="mx-auto mt-6 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          More options<ChevronDown className={cn("h-3.5 w-3.5 transition-transform", moreOpts && "rotate-180")} />
        </button>
        {moreOpts && (
          <div className="mt-4 animate-in fade-in space-y-5">
            <div>
              <p className="mb-2 text-center text-sm text-muted-foreground">Who to reach</p>
              <PillToggle single noAdd options={[{ v: "new", label: "New people" }, { v: "existing", label: "My connections" }]} value={[edit.reach]} onChange={(v) => v[0] && setEdit({ ...edit, reach: v[0] as "new" | "existing" })} />
            </div>
            {isProduct && (
              <div>
                <p className="mb-2 text-center text-sm text-muted-foreground">Pricing in messages</p>
                <PillToggle single noAdd options={[{ v: "no", label: "Keep private" }, { v: "yes", label: "Share price" }]} value={[edit.pricing.disclose ? "yes" : "no"]} onChange={(v) => v[0] && setEdit({ ...edit, pricing: { ...edit.pricing, disclose: v[0] === "yes" } })} />
                {edit.pricing.disclose && <Input className="mt-3 h-12 rounded-xl" value={edit.pricing.text} onChange={(e) => setEdit({ ...edit, pricing: { ...edit.pricing, text: e.target.value } })} placeholder="e.g. Starts at ₹15,000/month" />}
              </div>
            )}
          </div>
        )}
      </div>
    );
    footer = (
      <Button size="lg" onClick={saveEdit} disabled={saving} className="h-12 w-full gap-2 rounded-xl text-base">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Save changes
      </Button>
    );
  } else if (phase === "goal") {
    body = (
      <Question title="What should this campaign do?">
        <div className="grid grid-cols-2 gap-3">{GOALS.map((g) => <GoalTile key={g.v} icon={g.icon} label={g.label} active={goal === g.v} onClick={() => chooseGoal(g.v)} />)}</div>
      </Question>
    );
  } else if (phase === "q" && q) {
    body = <div key={`${goal}-${step}`}>{renderQuestion(q)}</div>;
    if (!autoAdvance || isLast) {
      const done = isEdit && (editMode === "one" || isLast);
      footer = (
        <Button size="lg" onClick={next} disabled={!canNext} className="h-12 w-full gap-2 rounded-xl text-base">
          {done ? <><Check className="h-4 w-4" />Done</> : isLast ? <><Sparkles className="h-4 w-4" />Show my campaigns</> : <>Continue<ArrowRight className="h-4 w-4" /></>}
        </Button>
      );
    }
  } else if (phase === "suggest") {
    body = loading ? <BuildingState /> : (
      <Question title={suggestions.length ? "Pick a campaign" : "Nothing yet"}>
        {suggestions.length ? (
          <div className="space-y-3">
            {suggestions.map((s, i) => {
              const line = [s.buyer_titles?.slice(0, 2).join(", "), s.buyer_industries?.[0], s.region].filter(Boolean).join(" · ");
              return (
                <button key={`${s.title}-${i}`} type="button" onClick={() => pick(s)} style={{ animationDelay: `${i * 100}ms` }}
                  className="group flex w-full min-w-0 max-w-full animate-in fade-in slide-in-from-bottom-2 fill-mode-both items-center gap-4 overflow-hidden rounded-2xl border p-5 text-left transition-colors hover:border-primary hover:bg-primary/5">
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 break-words font-semibold leading-snug">{s.title}</p>
                    <p className="mt-1 truncate text-sm text-muted-foreground">{line}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                </button>
              );
            })}
            <button type="button" onClick={() => build(true)} className="mx-auto mt-2 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><RefreshCw className="h-3.5 w-3.5" />Show different ones</button>
          </div>
        ) : (
          <div className="text-center"><Button variant="outline" onClick={() => build(true)} className="gap-1.5"><RefreshCw className="h-4 w-4" />Try again</Button></div>
        )}
      </Question>
    );
  } else if (phase === "finish" && picked && req) {
    const industry = picked.buyer_industries?.length && (goal === "sell" || goal === "partners" || goal === "job_search") ? picked.buyer_industries.slice(0, 2).join(", ") : "";
    body = (
      <div className="animate-in fade-in slide-in-from-bottom-2 space-y-6 duration-300">
        <p className="text-center text-xl font-semibold leading-snug tracking-tight">
          We'll reach <span className="text-primary">{titles.slice(0, 2).join(", ") || "the right people"}</span>
          {industry ? <> at <span className="text-primary">{industry}</span> companies</> : null}
          {" "}in <span className="text-primary">{req.countries.join(", ")}</span>
          {req.products[0] ? <> about <span className="text-primary">{shortProductName(req.products[0])}</span></> : null}.
        </p>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">First message</p>
            <button type="button" onClick={() => loadPreview()} disabled={previewing} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50">
              <RefreshCw className={cn("h-3 w-3", previewing && "animate-spin")} />Rewrite
            </button>
          </div>
          <div className="rounded-2xl rounded-tl-sm bg-muted px-4 py-3">
            {previewing && !preview
              ? <div className="space-y-2 py-1"><div className="h-3 w-full animate-pulse rounded bg-muted-foreground/15" /><div className="h-3 w-4/5 animate-pulse rounded bg-muted-foreground/15" /><div className="h-3 w-3/5 animate-pulse rounded bg-muted-foreground/15" /></div>
              : <p className="whitespace-pre-wrap text-sm leading-relaxed">{preview || "—"}</p>}
          </div>
        </div>

        <div>
          <button type="button" onClick={() => setAdjust((v) => !v)} className="mx-auto flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            Adjust<ChevronDown className={cn("h-3.5 w-3.5 transition-transform", adjust && "rotate-180")} />
          </button>
          {adjust && (
            <div className="mt-4 animate-in fade-in space-y-4">
              <div className="space-y-1.5">
                <p className="text-sm text-muted-foreground">Job titles</p>
                <ChipInput value={titles} onChange={setTitles} placeholder="Add a title" suggestions={picked.buyer_titles} aria-label="Job titles" />
              </div>
              <div className="space-y-1.5">
                <p className="text-sm text-muted-foreground">Change something</p>
                <div className="flex gap-2">
                  <Input value={refineText} onChange={(e) => setRefineText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") refine(); }} placeholder='e.g. "only owners" or "bigger companies"' />
                  <Button variant="outline" onClick={refine} disabled={refining || !refineText.trim()} className="shrink-0">{refining ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update"}</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
    footer = (
      <Button size="lg" onClick={save} disabled={saving} className="h-12 w-full gap-2 rounded-xl text-base">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Save campaign
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(680px,92vh)] max-w-[560px] flex-col gap-0 overflow-hidden p-0 [&>div:last-of-type]:min-h-0 [&>div:last-of-type]:flex-1">
        <DialogTitle className="sr-only">{isEdit ? "Edit campaign" : "New campaign strategy"}</DialogTitle>
        <DialogDescription className="sr-only">Answer a few quick questions to get ready campaigns.</DialogDescription>
        <StepShell total={total} current={current} onBack={(ctx || phase === "goal") && (isEdit ? phase !== "review" && !!edit : phase !== "goal" && !(phase === "suggest" && loading)) ? back : undefined} footer={footer}>
          {body}
        </StepShell>
      </DialogContent>
    </Dialog>
  );
}
